import { SiteHeader } from "@/components/site-header"
import { CodeBlock } from "@/components/code-block"

export default function AddFieldsGuidePage() {
  return (
    <>
      <SiteHeader title="Guía: Agregar campos a un modelo existente" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-5xl space-y-10">

          {/* ─── Objetivo ─── */}
          <section>
            <h2 className="text-base font-semibold mb-2">Objetivo</h2>
            <p className="text-sm text-muted-foreground">
              Guía paso a paso para agregar columnas nuevas a una entidad existente, con relaciones FK incluidas.
              El caso de referencia es agregar <code className="text-xs font-mono">sgt_code</code> (campo simple)
              y <code className="text-xs font-mono">cargo_type_id</code> (FK a <code className="text-xs font-mono">cargo_types</code>)
              al modelo <code className="text-xs font-mono">cargoProducts</code>.
              El mismo flujo aplica a cualquier entidad del sistema.
            </p>
          </section>

          {/* ─── Flujo ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Flujo general</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Cada campo nuevo debe atravesar <span className="font-medium">5 capas</span> en orden.
              Saltarse alguna rompe la type-safety de extremo a extremo.
            </p>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-2 font-medium text-xs">Paso</th>
                    <th className="text-left px-4 py-2 font-medium text-xs">Archivo</th>
                    <th className="text-left px-4 py-2 font-medium text-xs">¿Qué cambia?</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-2 text-xs font-medium">1 · Schema DB</td>
                    <td className="px-4 py-2"><code className="text-xs font-mono">apps/api/src/db/schema.ts</code></td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">Columna Drizzle</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-xs font-medium">2 · Migración SQL</td>
                    <td className="px-4 py-2"><code className="text-xs font-mono">apps/api/src/db/migrations/XXXX_*.sql</code></td>
                    <td className="px-4 py-2 text-xs text-muted-foreground"><code className="text-xs font-mono">ALTER TABLE</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-xs font-medium">3 · Zod schema</td>
                    <td className="px-4 py-2"><code className="text-xs font-mono">packages/shared/src/schemas/cargos.ts</code></td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">Campo Zod + tipo TS</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-xs font-medium">4 · Ruta API</td>
                    <td className="px-4 py-2"><code className="text-xs font-mono">apps/api/src/routes/cargos.ts</code></td>
                    <td className="px-4 py-2 text-xs text-muted-foreground"><code className="text-xs font-mono">.select()</code> / <code className="text-xs font-mono">.values()</code> / <code className="text-xs font-mono">.returning()</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-xs font-medium">5 · UI</td>
                    <td className="px-4 py-2"><code className="text-xs font-mono">apps/web/src/pages/cargos/CargoProductsPage.tsx</code></td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">Tabla + Sheet (formulario)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ─── 1. Schema DB ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">
              1 · Schema DB — <code className="text-xs font-mono">apps/api/src/db/schema.ts</code>
            </h2>
            <p className="text-sm text-muted-foreground mb-3">
              Agregá las columnas dentro de <code className="text-xs font-mono">pgTable</code>. Las columnas nuevas sobre datos existentes
              deben ser <span className="font-medium">nullable</span> (o tener <code className="text-xs font-mono">DEFAULT</code>)
              para no romper filas ya insertadas.
              Usá <code className="text-xs font-mono">onDelete: 'set null'</code> en FKs para preservar el producto si el tipo de carga es eliminado.
            </p>
            <CodeBlock code={`export const cargoProducts = pgTable('products', {
  id:          uuid('id').primaryKey().$defaultFn(() => uuidv7()),
  code:        varchar('code', { length: 10 }).notNull().unique(),
  name:        varchar('name', { length: 200 }).notNull(),

  // ── Campos nuevos ──────────────────────────────────────────────
  sgtCode:     varchar('sgt_code', { length: 20 }),               // nullable: no todos tienen código SGT
  cargoTypeId: uuid('cargo_type_id').references(() => cargoTypes.id, {
                 onDelete: 'set null',
               }),
  // ───────────────────────────────────────────────────────────────

  deletedAt: timestamp('deleted_at', { withTimezone: true }),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow().notNull(),
})

// El tipo inferido incluye sgtCode y cargoTypeId automáticamente
export type CargoProduct = typeof cargoProducts.$inferSelect`} />

            <p className="text-sm text-muted-foreground mt-4 mb-1 font-medium">Comandos tras el cambio:</p>
            <CodeBlock code={`# Desarrollo — sincroniza directo contra la DB (sin generar .sql)
cd apps/api && bun run db:push

# Producción — genera el archivo SQL y luego lo aplica
cd apps/api && bun run db:generate
cd apps/api && bun run db:migrate`} />
          </section>

          {/* ─── 2. Migración SQL ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">
              2 · Migración SQL — <code className="text-xs font-mono">0002_cargo_products_sgt_code_and_type.sql</code>
            </h2>
            <p className="text-sm text-muted-foreground mb-3">
              Generalo con <code className="text-xs font-mono">bun run db:generate</code> o crealo manualmente.
              La convención de nombre es <code className="text-xs font-mono">XXXX_descripcion-corta.sql</code>.
            </p>
            <CodeBlock code={`-- =============================================================================
-- CDC-SCCR: Add sgt_code and cargo_type_id to products
-- =============================================================================

ALTER TABLE "products"
  ADD COLUMN "sgt_code" varchar(20),
  ADD COLUMN "cargo_type_id" uuid REFERENCES "cargo_types"("id") ON DELETE SET NULL;`} />
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-3">
              ⚠️ En el <code className="text-xs font-mono">.sql</code> nunca uses{" "}
              <code className="text-xs font-mono">DEFAULT uuid_generate_v7()</code> — no existe en PostgreSQL estándar.
              El <code className="text-xs font-mono">$defaultFn(() =&gt; uuidv7())</code> de Drizzle se aplica en la{" "}
              <span className="font-medium">app</span> antes del INSERT, no en la DB.
            </p>
          </section>

          {/* ─── 3. Zod schema ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">
              3 · Zod schema — <code className="text-xs font-mono">packages/shared/src/schemas/cargos.ts</code>
            </h2>
            <p className="text-sm text-muted-foreground mb-3">
              Toda la validación vive en <code className="text-xs font-mono">packages/shared</code> para ser compartida
              entre API, web y eventual mobile. Los campos opcionales usan{" "}
              <code className="text-xs font-mono">.optional().nullable()</code>:
              <code className="text-xs font-mono">optional()</code> permite omitir el campo en el JSON y{" "}
              <code className="text-xs font-mono">nullable()</code> permite enviarlo como{" "}
              <code className="text-xs font-mono">null</code> para "quitar" la relación.
            </p>
            <CodeBlock code={`export const createCargoSchema = z.object({
  name:        z.string().min(1, "El nombre es requerido").max(200),
  code:        z.string().min(1, "El código es requerido").max(10).toUpperCase(),

  // Campos nuevos — opcionales (nullable en DB)
  sgtCode:     z.string().max(20).optional().nullable(),
  cargoTypeId: z.string().uuid("ID de tipo de carga inválido").optional().nullable(),
});

// ⚠️ SIEMPRE usar .partial() — nunca definir updateSchema a mano con campos .optional()
// Si se agrega un campo al createSchema, el updateSchema lo hereda automáticamente.
export const updateCargoSchema = createCargoSchema.partial();

// Los tipos TypeScript ya existentes se actualizan solos por inferencia
export type CreateCargoInput = z.infer<typeof createCargoSchema>;
export type UpdateCargoInput = z.infer<typeof updateCargoSchema>;`} />

            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-3">
              ⚠️ Si la columna en DB es <code className="text-xs font-mono">varchar(20)</code>, el schema Zod debe tener{" "}
              <code className="text-xs font-mono">.max(20)</code> y el{" "}
              <code className="text-xs font-mono">&lt;Input maxLength&#123;20&#125;&gt;</code> en el formulario.
              No dejar <code className="text-xs font-mono">max(50)</code> que pasa Zod pero falla en PostgreSQL con 500.
            </p>
          </section>

          {/* ─── 4. Ruta API ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">
              4 · Ruta API — <code className="text-xs font-mono">apps/api/src/routes/cargos.ts</code>
            </h2>
            <p className="text-sm text-muted-foreground mb-3">
              Hay <span className="font-medium">tres lugares</span> donde los campos nuevos deben aparecer
              en cada endpoint: el <code className="text-xs font-mono">.select(&#123;&#125;)</code> del GET,
              y el <code className="text-xs font-mono">.returning(&#123;&#125;)</code> del POST y PATCH.
              En el GET también hay que agregar el <code className="text-xs font-mono">leftJoin</code> para
              traer el nombre del tipo junto al ID.
            </p>

            <p className="text-sm font-medium mb-2">4a · Imports — agregar <code className="text-xs font-mono">leftJoin</code></p>
            <CodeBlock code={`// Antes
import { eq, isNull, isNotNull, sql } from "drizzle-orm";

// Después — agregar leftJoin
import { eq, isNull, isNotNull, sql, leftJoin } from "drizzle-orm";`} />

            <p className="text-sm font-medium mt-6 mb-2">4b · GET <code className="text-xs font-mono">/products</code> — lista con LEFT JOIN</p>
            <p className="text-sm text-muted-foreground mb-3">
              Se usa <code className="text-xs font-mono">LEFT JOIN</code> (no <code className="text-xs font-mono">INNER</code>) porque{" "}
              <code className="text-xs font-mono">cargoTypeId</code> es nullable: con INNER JOIN los productos sin tipo de carga desaparecerían del resultado.
            </p>
            <CodeBlock code={`// ANTES
const baseQuery = db
  .select({
    id:        cargoProducts.id,
    code:      cargoProducts.code,
    name:      cargoProducts.name,
    deletedAt: cargoProducts.deletedAt,
    createdAt: cargoProducts.createdAt,
    updatedAt: cargoProducts.updatedAt,
  })
  .from(cargoProducts);

// DESPUÉS — agrega sgtCode, cargoTypeId y cargoTypeName vía LEFT JOIN
const baseQuery = db
  .select({
    id:            cargoProducts.id,
    code:          cargoProducts.code,
    name:          cargoProducts.name,
    sgtCode:       cargoProducts.sgtCode,
    cargoTypeId:   cargoProducts.cargoTypeId,
    cargoTypeName: cargoTypes.name,           // viene del JOIN
    deletedAt:     cargoProducts.deletedAt,
    createdAt:     cargoProducts.createdAt,
    updatedAt:     cargoProducts.updatedAt,
  })
  .from(cargoProducts)
  .leftJoin(cargoTypes, eq(cargoProducts.cargoTypeId, cargoTypes.id));`} />

            <p className="text-sm font-medium mt-6 mb-2">4c · POST <code className="text-xs font-mono">/products</code> — agregar campos al <code className="text-xs font-mono">.returning()</code></p>
            <p className="text-sm text-muted-foreground mb-3">
              El <code className="text-xs font-mono">.values(data)</code> ya funciona sin cambios: Drizzle mapea el objeto
              validado por Zod al schema y omite los campos <code className="text-xs font-mono">undefined</code>,
              dejando el <code className="text-xs font-mono">NULL</code> de la columna.
            </p>
            <CodeBlock code={`const [created] = await db
  .insert(cargoProducts)
  .values(data)                        // data ahora puede traer sgtCode y cargoTypeId
  .returning({
    id:           cargoProducts.id,
    code:         cargoProducts.code,
    name:         cargoProducts.name,
    sgtCode:      cargoProducts.sgtCode,      // ← nuevo
    cargoTypeId:  cargoProducts.cargoTypeId,  // ← nuevo
    deletedAt:    cargoProducts.deletedAt,
    createdAt:    cargoProducts.createdAt,
    updatedAt:    cargoProducts.updatedAt,
  });`} />

            <p className="text-sm font-medium mt-6 mb-2">4d · PATCH <code className="text-xs font-mono">/products/:id</code> — mismo patrón</p>
            <CodeBlock code={`const [updated] = await db
  .update(cargoProducts)
  .set({ ...data, updatedAt: new Date() })
  .where(eq(cargoProducts.id, id))
  .returning({
    id:           cargoProducts.id,
    code:         cargoProducts.code,
    name:         cargoProducts.name,
    sgtCode:      cargoProducts.sgtCode,      // ← nuevo
    cargoTypeId:  cargoProducts.cargoTypeId,  // ← nuevo
    deletedAt:    cargoProducts.deletedAt,
    createdAt:    cargoProducts.createdAt,
    updatedAt:    cargoProducts.updatedAt,
  });`} />

            <p className="text-sm text-muted-foreground mt-3">
              Los endpoints <code className="text-xs font-mono">DELETE</code> y{" "}
              <code className="text-xs font-mono">PATCH /:id/restore</code> solo devuelven{" "}
              <code className="text-xs font-mono">&#123; ok: true &#125;</code> — no requieren cambios.
            </p>

            <p className="text-sm font-medium mt-6 mb-2">4e · Validar FK antes del INSERT (opcional pero recomendado)</p>
            <p className="text-sm text-muted-foreground mb-3">
              Sin esta validación, un UUID inexistente provoca un 500 por violación de FK constraint.
              Con ella se devuelve un 404 con mensaje claro.
            </p>
            <CodeBlock code={`if (data.cargoTypeId) {
  const [type] = await db
    .select({ id: cargoTypes.id })
    .from(cargoTypes)
    .where(eq(cargoTypes.id, data.cargoTypeId))
    .limit(1);
  if (!type) return c.json({ error: "Tipo de carga no encontrado" }, 404);
}`} />
          </section>

          {/* ─── 5. Frontend ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">
              5 · Frontend — <code className="text-xs font-mono">apps/web/src/pages/cargos/CargoProductsPage.tsx</code>
            </h2>

            <p className="text-sm font-medium mb-2">5a · Actualizar la interfaz <code className="text-xs font-mono">CargoProductRow</code></p>
            <CodeBlock code={`// ANTES
interface CargoProductRow {
  id:        string;
  name:      string;
  code:      string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// DESPUÉS
interface CargoProductRow {
  id:            string;
  name:          string;
  code:          string;
  sgtCode:       string | null;   // nuevo
  cargoTypeId:   string | null;   // nuevo
  cargoTypeName: string | null;   // viene del LEFT JOIN del API
  deletedAt:     string | null;
  createdAt:     string;
  updatedAt:     string;
}`} />

            <p className="text-sm font-medium mt-6 mb-2">5b · Actualizar <code className="text-xs font-mono">FormState</code> y <code className="text-xs font-mono">EMPTY_FORM</code></p>
            <p className="text-sm text-muted-foreground mb-3">
              Los campos del formulario se guardan como <code className="text-xs font-mono">string</code> internamente
              (vacío = sin valor). Se convierten a <code className="text-xs font-mono">null</code> en el{" "}
              <code className="text-xs font-mono">handleSubmit</code> antes de enviar.
            </p>
            <CodeBlock code={`type FormState = {
  name:        string;
  code:        string;
  sgtCode:     string;
  cargoTypeId: string;  // "" = sin tipo (se convierte a null al enviar)
};

const EMPTY_FORM: FormState = {
  name:        "",
  code:        "",
  sgtCode:     "",
  cargoTypeId: "",
};`} />

            <p className="text-sm font-medium mt-6 mb-2">5c · Query para el selector de tipos</p>
            <p className="text-sm text-muted-foreground mb-3">
              Agregá esta query junto a la de <code className="text-xs font-mono">cargoProducts</code>:
            </p>
            <CodeBlock code={`const { data: cargoTypesList = [] } = useQuery({
  queryKey: ["cargos", "types"],
  queryFn: async () => {
    const res = await api.cargos.types.$get({ query: { deleted: "false" } });
    if (!res.ok) throw new Error("Error loading cargo types");
    return res.json() as Promise<{ id: string; code: string; name: string }[]>;
  },
});`} />

            <p className="text-sm font-medium mt-6 mb-2">5d · Actualizar <code className="text-xs font-mono">openEdit</code></p>
            <CodeBlock code={`function openEdit(item: CargoProductRow) {
  setEditing(item);
  setForm({
    name:        item.name,
    code:        item.code,
    sgtCode:     item.sgtCode ?? "",
    cargoTypeId: item.cargoTypeId ?? "",
  });
  setFormError(null);
  setSheetOpen(true);
}`} />

            <p className="text-sm font-medium mt-6 mb-2">5e · Actualizar <code className="text-xs font-mono">handleSubmit</code></p>
            <CodeBlock code={`function handleSubmit(e: React.FormEvent) {
  e.preventDefault();
  setFormError(null);

  const payload = {
    name:        form.name.trim(),
    code:        form.code.trim().toUpperCase(),
    sgtCode:     form.sgtCode.trim() || null,   // string vacío → null
    cargoTypeId: form.cargoTypeId || null,       // string vacío → null
  };

  if (!payload.name || !payload.code) {
    setFormError("Completá nombre y código");
    return;
  }

  if (editing) updateMut.mutate({ id: editing.id, data: payload });
  else createMut.mutate(payload);
}`} />

            <p className="text-sm font-medium mt-6 mb-2">5f · Columnas nuevas en la tabla</p>
            <CodeBlock code={`// TableHeader
<TableRow>
  <TableHead className="w-32">Código</TableHead>
  <TableHead>Nombre</TableHead>
  <TableHead className="w-32">Código SGT</TableHead>    {/* nuevo */}
  <TableHead className="w-40">Tipo de carga</TableHead>  {/* nuevo */}
  {isAdmin && <TableHead className="w-32 text-right pr-4">Acciones</TableHead>}
</TableRow>

// TableBody — dentro del map de filas
<TableCell className="text-sm text-muted-foreground font-mono">
  {item.sgtCode ?? <span className="italic text-xs text-muted-foreground/40">—</span>}
</TableCell>

<TableCell className="text-sm">
  {item.cargoTypeName
    ? <span className="bg-muted px-2 py-0.5 rounded text-xs">{item.cargoTypeName}</span>
    : <span className="italic text-xs text-muted-foreground/40">Sin tipo</span>
  }
</TableCell>`} />

            <p className="text-sm font-medium mt-6 mb-2">5g · Campos nuevos en el Sheet (formulario)</p>
            <p className="text-sm text-muted-foreground mb-3">
              Agregá estos dos bloques después del campo "Nombre" dentro del{" "}
              <code className="text-xs font-mono">&lt;form&gt;</code>.
            </p>
            <CodeBlock code={`{/* Input: código SGT */}
<div className="grid gap-1.5">
  <Label htmlFor="cargo-sgt-code">Código SGT</Label>
  <Input
    id="cargo-sgt-code"
    value={form.sgtCode}
    onChange={(e) => setForm((f) => ({ ...f, sgtCode: e.target.value }))}
    maxLength={20}
    className="font-mono"
    placeholder="Ej: 10230012"
  />
  <p className="text-xs text-muted-foreground">Opcional. Código del sistema de origen SGT.</p>
</div>

{/* Select: tipo de carga */}
<div className="grid gap-1.5">
  <Label htmlFor="cargo-type-select">Tipo de carga</Label>
  <Select
    value={form.cargoTypeId}
    onValueChange={(v) => setForm((f) => ({ ...f, cargoTypeId: v === "__none__" ? "" : v }))}
  >
    <SelectTrigger id="cargo-type-select">
      {/*
        ⚠️ Pasar siempre el nombre calculado como children de SelectValue.
        Sin esto, shadcn/ui muestra el UUID crudo cuando hay un valor seleccionado.
      */}
      <SelectValue placeholder="Sin tipo">
        {form.cargoTypeId
          ? (cargoTypesList.find((t) => t.id === form.cargoTypeId)?.name ?? form.cargoTypeId)
          : null}
      </SelectValue>
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="__none__">Sin tipo</SelectItem>
      {cargoTypesList.map((t) => (
        <SelectItem key={t.id} value={t.id}>
          {t.name}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
</div>`} />
          </section>

          {/* ─── 6. Tests ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">
              6 · Tests — <code className="text-xs font-mono">packages/shared/src/schemas/cargos.test.ts</code>
            </h2>
            <p className="text-sm text-muted-foreground mb-3">
              Agregá estos casos al archivo existente sin modificar los tests que ya pasan.
            </p>
            <CodeBlock code={`describe("createCargoSchema — campos nuevos", () => {
  it("acepta payload sin sgtCode ni cargoTypeId", () => {
    const result = createCargoSchema.safeParse({ code: "ABC", name: "Demo" });
    expect(result.success).toBe(true);
  });

  it("acepta sgtCode con valor", () => {
    const result = createCargoSchema.safeParse({ code: "ABC", name: "Demo", sgtCode: "10230012" });
    expect(result.success).toBe(true);
    if (result.success) expect(result.data.sgtCode).toBe("10230012");
  });

  it("rechaza sgtCode de más de 20 caracteres", () => {
    const result = createCargoSchema.safeParse({ code: "ABC", name: "Demo", sgtCode: "X".repeat(21) });
    expect(result.success).toBe(false);
  });

  it("acepta cargoTypeId UUID válido", () => {
    const result = createCargoSchema.safeParse({
      code: "ABC", name: "Demo",
      cargoTypeId: "019f1500-aaaa-7000-8000-000000000123",
    });
    expect(result.success).toBe(true);
  });

  it("rechaza cargoTypeId que no es UUID", () => {
    const result = createCargoSchema.safeParse({ code: "ABC", name: "Demo", cargoTypeId: "no-es-uuid" });
    expect(result.success).toBe(false);
  });

  it("acepta cargoTypeId null (quitar tipo)", () => {
    const result = createCargoSchema.safeParse({ code: "ABC", name: "Demo", cargoTypeId: null });
    expect(result.success).toBe(true);
  });
});`} />
            <CodeBlock code={`# Correr los tests
cd packages/shared && bun test`} />
          </section>

          {/* ─── 7. Checklist ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">7 · Checklist de verificación</h2>
            <div className="rounded-lg border divide-y text-sm">
              {[
                ["schema.ts", "columnas sgtCode y cargoTypeId agregadas con tipos correctos"],
                ["DB sync", "bun run db:push (dev) o db:generate + db:migrate (prod) sin errores"],
                ["cargos.ts (shared)", "createCargoSchema y updateCargoSchema tienen los nuevos campos"],
                ["Tests shared", "bun test — todos los casos pasan"],
                ["cargos.ts (api) GET", "select incluye sgtCode, cargoTypeId, cargoTypeName + leftJoin"],
                ["cargos.ts (api) POST", ".returning() incluye sgtCode y cargoTypeId"],
                ["cargos.ts (api) PATCH", ".returning() incluye sgtCode y cargoTypeId"],
                ["bun run typecheck", "sin errores de TypeScript en todo el monorepo"],
                ["CargoProductRow", "interfaz actualizada con sgtCode, cargoTypeId, cargoTypeName"],
                ["FormState + EMPTY_FORM", "campos sgtCode y cargoTypeId agregados"],
                ["query cargoTypesList", "useQuery para el selector de tipos agregada"],
                ["openEdit", "puebla sgtCode y cargoTypeId desde item"],
                ["handleSubmit", "convierte string vacío a null antes de enviar"],
                ["Tabla", "columnas 'Código SGT' y 'Tipo de carga' visibles"],
                ["Sheet", "campos Input (sgtCode) y Select (cargoTypeId) en el formulario"],
                ["bun run dev", "la app corre, el formulario guarda y la tabla muestra los datos"],
              ].map(([file, desc]) => (
                <div key={file} className="flex items-start gap-3 px-4 py-2.5">
                  <span className="mt-0.5 size-4 shrink-0 rounded border border-muted-foreground/30 bg-muted" />
                  <div>
                    <span className="font-medium text-xs">{file}</span>
                    <span className="text-xs text-muted-foreground"> — {desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ─── 8. Errores comunes ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">8 · Errores comunes y cómo evitarlos</h2>
            <div className="space-y-4">

              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
                <p className="text-sm font-medium text-destructive mb-1">
                  ❌ <code className="text-xs font-mono">Column "sgt_code" of relation "products" does not exist</code>
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Causa:</span> modificaste <code className="text-xs font-mono">schema.ts</code> pero no corriste <code className="text-xs font-mono">db:push</code> / <code className="text-xs font-mono">db:migrate</code>.{" "}
                  <span className="font-medium">Solución:</span> <code className="text-xs font-mono">cd apps/api && bun run db:push</code>
                </p>
              </div>

              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
                <p className="text-sm font-medium text-destructive mb-1">
                  ❌ El <code className="text-xs font-mono">&lt;Select&gt;</code> muestra el UUID en lugar del nombre
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Causa:</span> <code className="text-xs font-mono">shadcn/ui</code> usa el <code className="text-xs font-mono">value</code> crudo si no le pasás <code className="text-xs font-mono">children</code> a{" "}
                  <code className="text-xs font-mono">&lt;SelectValue&gt;</code>.{" "}
                  <span className="font-medium">Solución:</span> siempre calculá el nombre desde la lista y pasalo como children (ver paso 5g).
                </p>
              </div>

              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
                <p className="text-sm font-medium text-destructive mb-1">
                  ❌ TypeScript pierde el tipo del query Drizzle con <code className="text-xs font-mono">.where()</code> condicional
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Causa:</span> reasignar con <code className="text-xs font-mono">let</code> + <code className="text-xs font-mono">if</code>.{" "}
                  <span className="font-medium">Solución:</span> usar siempre el ternario con <code className="text-xs font-mono">const</code>:
                </p>
                <CodeBlock code={`const listQuery = whereCondition ? baseQuery.where(whereCondition) : baseQuery;`} />
              </div>

              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
                <p className="text-sm font-medium text-destructive mb-1">
                  ❌ <code className="text-xs font-mono">updateSchema</code> no valida el campo nuevo
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Causa:</span> se definió a mano sin <code className="text-xs font-mono">.partial()</code> y no heredó los nuevos campos.{" "}
                  <span className="font-medium">Solución:</span> usar siempre <code className="text-xs font-mono">createCargoSchema.partial()</code>.
                </p>
              </div>

              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
                <p className="text-sm font-medium text-destructive mb-1">
                  ❌ <code className="text-xs font-mono">500 Internal Server Error</code> al crear con un <code className="text-xs font-mono">cargoTypeId</code> inexistente
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Causa:</span> FK constraint violation — la DB verifica que el UUID referenciado exista en <code className="text-xs font-mono">cargo_types</code>.{" "}
                  <span className="font-medium">Solución:</span> validar antes del INSERT (ver paso 4e) para devolver un 404 con mensaje claro.
                </p>
              </div>

              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
                <p className="text-sm font-medium text-destructive mb-1">
                  ❌ <code className="text-xs font-mono">cargoTypeName: null</code> para todos los registros aunque haya tipo asignado
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Causa:</span> se usó <code className="text-xs font-mono">INNER JOIN</code> y los productos sin tipo desaparecieron, o la condición del join apunta a la columna incorrecta.{" "}
                  <span className="font-medium">Solución:</span> verificar que sea <code className="text-xs font-mono">leftJoin(cargoTypes, eq(cargoProducts.cargoTypeId, cargoTypes.id))</code>.
                </p>
              </div>

            </div>
          </section>

        </div>
      </div>
    </>
  )
}
